import { useState, useRef } from "react"; // Remove useEffect since it's not used
import useRealTime from "./useRealtime";
import useAudioRecorder from "./useAudioRecorder";
import useAudioPlayer from "./useAudioPlayer";
import type { GroundingFile, ChatMessage } from "@/types";
import type { Message } from "@/types"; // Ensure Message is correctly imported
import type { ResponseErrorMessage } from "@/types"; // Import the correct error type
import { useRealtimeContext } from "../context/RealtimeContext";

const TRANSLATOR_KEY = import.meta.env.VITE_AZURE_TRANSLATOR_KEY || "5bN42Hnzu8xrvaCGe6ffLZCjDIjJ5nTjbuHEHfB6gfPXSS3FkJlWJQQJ99BBACmepeSXJ3w3AAAbACOGmaWN";
const TRANSLATOR_REGION = import.meta.env.VITE_AZURE_TRANSLATOR_REGION || "uksouth";

export default function useVoiceConversation() {
    const [isRecording, setIsRecording] = useState(false);
    const [messages] = useState<ChatMessage[]>([]);
    const groundingFiles = useState<GroundingFile[]>([])[0];

    const [userPartial, setUserPartial] = useState("");
    const [assistantPartial, setAssistantPartial] = useState<string>("");

    // 1) Our pull in single source of truth for the sessionContext
    const { sessionContext } = useRealtimeContext();
    const connectionId = sessionContext.connectionId;
    //console.log("[useVoiceConversation] Using RealtimeProvider's connectionId:", connectionId);

    const [latestTranscript, setLatestTranscript] = useState(""); // Add this line

    // Buffer for assistant audio chunks
    const assistantAudioChunks = useRef<Blob[]>([]);

    const { reset: resetAudioPlayer, play: playAudio, stop: stopAudioPlayer } = useAudioPlayer();

    const { startSession, addUserAudio, inputAudioBufferClear, endSession, closeWebSocket } = useRealTime({
        onWebSocketOpen: () => console.log("[useVoiceConversation] WS open"),
        onWebSocketClose: () => {
            console.log("[useVoiceConversation] WS closed, stopping recording");
            setIsRecording(false); // Force recording state to false on disconnect
        },
        onWebSocketError: event => console.error("[useVoiceConversation] WS error:", event),
        onReceivedError: (message: Message) => {
            const errorMessage = message as unknown as ResponseErrorMessage; // Type assertion
            console.error("[useVoiceConversation] LLM error:", {
                event_id: errorMessage.event_id, // Ensure this exists in ResponseErrorMessage
                error: errorMessage.error,
                raw_message: errorMessage
            });
        },
        onReceivedResponseAudioDelta: message => {
            playAudio(message.delta);
            assistantAudioChunks.current.push(base64ToBlob(message.delta));
        },
        onReceivedResponseDone: () => {
            //console.log("[useVoiceConversation] Assistant audio ended");
            // Once the assistant finishes, we could store the partial in a final array or reset
            //console.log("[useVoiceConversation] Assistant done speaking, partial:", assistantPartial);
            assistantAudioChunks.current = [];
        },
        connectionId, // Add this
        onReceivedResponseAudioTranscriptDelta: message => {
            //console.log("Received transcript delta:", message.delta);
            // Each incoming delta is appended to the partial transcript
            const deltaText = message.delta;

            // Update assistant partial and ensure it's incremental
            setAssistantPartial(prev => {
                const newText = prev + deltaText;
                //console.log("[useVoiceConversation] Updated assistant partial:", newText);
                return newText;
            });

            setLatestTranscript(prev => {
                // If we previously started with "**Assistant:** ", slice off 14 characters
                // (the length of that prefix including space)
                const prefix = "**Assistant:** ";
                if (prev.startsWith(prefix)) {
                    const currentText = prev.slice(prefix.length);
                    return `${prefix}${currentText}${message.delta}`;
                }
                return `${prefix}${message.delta}`;
            });
        },
        onReceivedInterrupt: () => {
            // When an interrupt event arrives from the backend, stop audio playback
            console.log("Interrupt event received – stopping audio playback.");
            stopAudioPlayer();
            resetAudioPlayer();
            assistantAudioChunks.current = []; // Clear buffered audio chunks
        },
        onReceivedInputAudioTranscriptionDelta: message => {
            // Each chunk of user text
            const chunk = message.delta;
            setUserPartial(prev => prev + chunk);
            console.log("[useVoiceConversation] User partial:", userPartial);
        },
        onReceivedInputAudioTranscriptionCompleted: message => {
            //console.log("[useVoiceConversation] Final user transcript:", message.transcript);
            setLatestTranscript(`**User:** ${message.transcript}`);
            setUserPartial("");
        },
        onReceivedInputAudioTranscriptionFailed: () => {
            //console.error("Audio transcription failed");
            setLatestTranscript(prev => {
                // Only update if it's a user message
                if (prev.startsWith("User: ")) {
                    return "Transcription failed. Please try again.";
                }
                return prev;
            });
        }
    });

    const { start: startAudioRecording, stop: stopAudioRecording } = useAudioRecorder({
        onAudioRecorded: audioData => {
            const finalBlob = typeof audioData === "string" ? base64ToBlob(audioData) : audioData;
            blobToBase64(finalBlob).then(base64Audio => {
                addUserAudio(base64Audio);
            });
        }
    });

    // NEW: Translation function that can be used when hovering over text
    async function translateText(text: string, targetLanguage: string): Promise<string> {
        try {
            console.log("Translation attempt:", {
                text,
                targetLanguage,
                translatorKey: TRANSLATOR_KEY?.substring(0, 6) + "...",
                translatorRegion: TRANSLATOR_REGION
            });

            const response = await fetch(`https://api.cognitive.microsofttranslator.com/translate?api-version=3.0&to=${targetLanguage}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Ocp-Apim-Subscription-Key": TRANSLATOR_KEY,
                    "Ocp-Apim-Subscription-Region": TRANSLATOR_REGION
                },
                body: JSON.stringify([{ Text: text }])
            });

            if (!response.ok) {
                console.error("Translation API error:", {
                    status: response.status,
                    statusText: response.statusText,
                    headers: Object.fromEntries(response.headers.entries())
                });
                throw new Error(`Translation failed with status: ${response.status}`);
            }

            const data = await response.json();
            console.log("Translation success:", data);
            return data[0]?.translations[0]?.text || text;
        } catch (error) {
            console.error("Translation error:", error);
            return text; // Return original text if translation fails
        }
    }

    return {
        isRecording,
        onToggleRecording: async () => {
            console.log("[useVoiceConversation] Toggle recording:", { isRecording, connectionId });
            if (!isRecording) {
                try {
                    // If you truly want to generate a new connection for each ‘start’,
                    // you can do it here by calling setSessionConnectionId(…) from context:
                    // setSessionConnectionId(crypto.randomUUID());
                    // but typically you won't do that unless you want a brand-new ID each time.
                    setLatestTranscript("");
                    await startSession();
                    resetAudioPlayer();
                    await startAudioRecording();
                    setIsRecording(true);
                    console.log("[useVoiceConversation] Recording started successfully");
                } catch (error) {
                    console.error("[useVoiceConversation] Error starting recording:", error);
                    setIsRecording(false);
                }
            } else {
                try {
                    await stopAudioRecording();
                    await stopAudioPlayer();
                    resetAudioPlayer();
                    inputAudioBufferClear();
                    endSession();
                    closeWebSocket();
                    setIsRecording(false);
                    // If you want to ‘reset’ the connection ID on stop:
                    // setSessionConnectionId(null);
                    console.log("[useVoiceConversation] Recording stopped successfully");
                } catch (error) {
                    console.error("[useVoiceConversation] Error stopping recording:", error);
                }
            }
        },
        groundingFiles,
        messages,
        translateText, // NEW: Expose translation function
        assistantPartial,
        latestTranscript, // Add this line
        connectionId // Add this to the return object
    };
}

/** Convert base64 string to Blob */
function base64ToBlob(base64Data: string): Blob {
    const byteString = atob(base64Data);
    const byteArray = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
        byteArray[i] = byteString.charCodeAt(i);
    }
    return new Blob([byteArray], { type: "audio/wav" });
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = function () {
            const dataUrl = reader.result as string;
            const base64 = dataUrl.split(",")[1] || "";
            resolve(base64);
        };
        reader.onerror = error => reject(error);
        reader.readAsDataURL(blob);
    });
}
